import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Loader2, Clock, CheckCircle2, Wrench } from "lucide-react";

/**
 * Reservas que quedaron SIN horario (la carga masiva perdió la hora y el catálogo tenía
 * varios horarios posibles ese día). El equipo les asigna el horario real acá.
 */
export function AsignarHorarioDialog({ open, onOpenChange, isAdmin = false, onDone }) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [elegido, setElegido] = useState({});
  const [guardando, setGuardando] = useState(null);
  const [reparando, setReparando] = useState(false);
  const [asignandoTodas, setAsignandoTodas] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reservas/sin-horario");
      setItems(data || []);
      setElegido(Object.fromEntries((data || []).map((r) => [r.id, (r.horarios_validos || [])[0] || ""])));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const asignar = async (r) => {
    const hora = elegido[r.id];
    if (!hora) {
      toast.error("Elegí un horario para esta reserva.");
      return;
    }
    setGuardando(r.id);
    try {
      await api.post(`/reservas/${r.id}/asignar-horario`, { hora });
      toast.success(`${r.pasajero?.nombre || "Reserva"} asignada a las ${hora}.`);
      await load();
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setGuardando(null);
    }
  };

  const reparar = async () => {
    setReparando(true);
    try {
      const { data } = await api.post("/reservas/salidas-medianoche/reparar");
      toast.success(
        `${data.reubicadas} reubicada(s) al horario real · ${data.a_revision} para asignar a mano · ` +
        `${data.salidas_borradas} salida(s) de 00:00 eliminada(s).`
      );
      await load();
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setReparando(false);
    }
  };

  const asignarTodas = async () => {
    const pares = items
      .filter((r) => elegido[r.id])
      .map((r) => ({ reserva_id: r.id, hora: elegido[r.id] }));
    if (pares.length === 0) {
      toast.error("No hay ningún horario elegido todavía.");
      return;
    }
    setAsignandoTodas(true);
    try {
      const { data } = await api.post("/reservas/asignar-horario-lote", { items: pares });
      if (data.fallidas > 0) {
        const motivos = data.detalle.filter((d) => !d.ok).map((d) => `${d.reserva_id}: ${d.motivo}`);
        toast.warning(`${data.aplicadas} asignada(s) · ${data.fallidas} no se pudieron asignar: ${motivos.join(" · ")}`);
      } else {
        toast.success(`${data.aplicadas} reserva(s) asignada(s) a su horario real.`);
      }
      await load();
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setAsignandoTodas(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="asignar-horario-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-slate-700" /> Reservas sin horario
          </DialogTitle>
          <DialogDescription>
            Estas reservas se cargaron sin hora válida (la carga masiva la perdió) y el tour tiene varios horarios
            ese día, así que el sistema no elige por su cuenta. Asignales el horario real: se mueven a la salida
            correcta y el cupo se recalcula al instante.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between gap-2">
          <span className="text-sm text-slate-600" data-testid="sin-horario-total">
            {loading ? "Cargando…" : `${items.length} reserva(s) sin horario`}
          </span>
          <div className="flex items-center gap-2">
            {items.length > 0 && (
              <Button
                size="sm"
                onClick={asignarTodas}
                disabled={asignandoTodas}
                className="gap-2"
                data-testid="asignar-todas-button"
              >
                {asignandoTodas ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                Asignar todas
              </Button>
            )}
            {isAdmin && (
              <Button
                variant="outline"
                size="sm"
                onClick={reparar}
                disabled={reparando}
                className="gap-2"
                data-testid="reparar-salidas-medianoche-button"
              >
                {reparando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wrench className="h-4 w-4" />}
                Reparar salidas de 00:00
              </Button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : items.length === 0 ? (
          <p className="py-8 text-center text-sm text-slate-500" data-testid="sin-horario-vacio">
            No hay reservas sin horario. Todo está asignado a su salida.
          </p>
        ) : (
          <div className="border rounded-md overflow-hidden">
            <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-white z-10">
                  <TableRow className="hover:bg-white">
                    {["Pasajero", "Código", "Tour", "Fecha", "Pax", "Horario", ""].map((h, i) => (
                      <TableHead key={i} className="text-xs uppercase tracking-wider text-slate-500">{h}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((r) => {
                    const pax = (r.adultos || 0) + (r.ninos || 0) + (r.infantes || 0);
                    const opciones = r.horarios_validos || [];
                    return (
                      <TableRow key={r.id} data-testid={`sin-horario-row-${r.id}`}>
                        <TableCell className="text-sm text-slate-800">
                          {r.pasajero?.nombre} {r.pasajero?.apellido}
                        </TableCell>
                        <TableCell className="text-xs font-mono text-slate-500">{r.codigo_booking}</TableCell>
                        <TableCell className="text-sm text-slate-600 max-w-[170px] truncate">{r.tour_nombre || "—"}</TableCell>
                        <TableCell className="text-sm font-mono text-slate-700">{r.fecha}</TableCell>
                        <TableCell className="text-sm text-slate-600 whitespace-nowrap">{pax} pax</TableCell>
                        <TableCell>
                          {opciones.length === 0 ? (
                            <span className="text-xs text-amber-700">Sin horarios de catálogo ese día</span>
                          ) : (
                            <Select
                              value={elegido[r.id] || ""}
                              onValueChange={(v) => setElegido((s) => ({ ...s, [r.id]: v }))}
                            >
                              <SelectTrigger className="h-8 w-[110px]" data-testid={`sin-horario-select-${r.id}`}>
                                <SelectValue placeholder="Horario" />
                              </SelectTrigger>
                              <SelectContent>
                                {opciones.map((h) => (
                                  <SelectItem key={h} value={h} data-testid={`sin-horario-opcion-${r.id}-${h}`}>{h}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            onClick={() => asignar(r)}
                            disabled={guardando === r.id || opciones.length === 0}
                            className="gap-1.5"
                            data-testid={`sin-horario-asignar-${r.id}`}
                          >
                            {guardando === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                            Asignar
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default AsignarHorarioDialog;
